import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  { id: "bireysel", label: "Bireysel", testid: "tier-select-bireysel" },
  { id: "kobi", label: "KOBİ", testid: "tier-select-kobi" },
  { id: "kurumsal", label: "Kurumsal", testid: "tier-select-kurumsal" },
];

const FAQS = [
  {
    q: "Akış hangi bankalarla entegre çalışıyor?",
    a: "Garanti BBVA, İş Bankası, Akbank, Yapı Kredi, QNB Finansbank ve Ziraat Bankası başta olmak üzere açık bankacılık altyapısı üzerinden tüm büyük Türk bankalarıyla senkronize çalışır.",
  },
  {
    q: "Finansal verilerim güvende mi?",
    a: "Tüm veriler 256-bit SSL ile şifrelenir, KVKK'ya tam uyumlu şekilde Türkiye'deki sunucularda saklanır. Banka şifreleriniz hiçbir zaman saklanmaz; bağlantı salt-okunur yetkiyle yapılır.",
  },
  {
    q: "Erken erişim ücretli mi?",
    a: "Hayır. Erken erişim listesine katılanlar lansman döneminde 3 ay ücretsiz kullanım ve ömür boyu %20 indirim hakkı kazanır.",
  },
  {
    q: "Muhasebe programımla entegre olur mu?",
    a: "E-fatura ve ön muhasebe entegrasyonları (Logo, Mikro, Luca ve Paraşüt) lansman paketine dahildir. Excel/CSV içe aktarımı ise ilk günden itibaren kullanılabilir.",
  },
];

const FaqItem = ({ faq, open, onToggle, testid }) => (
  <div className="border-b border-white/8">
    <button
      data-testid={testid}
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-4 py-5 text-left"
    >
      <span className="text-sm font-medium text-slate-200 sm:text-base">{faq.q}</span>
      <ChevronDown
        className={`h-4 w-4 shrink-0 text-emerald-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
      />
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p className="pb-5 text-sm leading-relaxed text-slate-400">{faq.a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const EarlyAccess = () => {
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("kobi");
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/early-access`, { email, tier });
      if (data.status === "exists") {
        toast.info("Bu e-posta adresi zaten erken erişim listesinde.");
      } else {
        toast.success("Tebrikler! Erken erişim listesine eklendiniz.");
        setEmail("");
      }
    } catch {
      toast.error("Bir sorun oluştu, lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="erken-erisim" data-testid="early-access-section" className="relative py-24 sm:py-32">
      <div className="pointer-events-none absolute left-1/2 top-10 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[160px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <div className="grid gap-16 lg:grid-cols-2">
          <Reveal>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
              Erken Erişim
            </div>
            <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              Finansının akışını ilk sen yönet.
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-slate-400 sm:text-lg">
              Lansman öncesi listeye katıl; 3 ay ücretsiz kullanım ve ömür boyu
              %20 indirim kazan.
            </p>

            <form onSubmit={submit} className="mt-9">
              <div className="mb-4 flex gap-2">
                {TIERS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={t.testid}
                    onClick={() => setTier(t.id)}
                    className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all duration-300 sm:text-sm ${
                      tier === t.id
                        ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-300"
                        : "border-white/12 text-slate-400 hover:border-white/25 hover:text-slate-200"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  data-testid="early-access-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="is@eposta-adresin.com"
                  className="w-full flex-1 rounded-full border border-white/12 bg-[#0D111A] px-6 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-emerald-400/60 focus:shadow-[0_0_24px_rgba(0,255,135,0.12)]"
                />
                <button
                  data-testid="early-access-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 text-sm font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_36px_rgba(0,255,135,0.4)] disabled:opacity-60"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Listeye Katıl
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
              </div>
              <p className="mt-3 text-xs text-slate-600">
                Spam yok. Sadece lansman haberleri ve erken erişim davetin.
              </p>
            </form>
          </Reveal>

          <Reveal delay={0.15}>
            <div data-testid="faq-accordion" className="rounded-2xl border border-white/8 bg-[#0D111A] px-7 py-3">
              {FAQS.map((faq, i) => (
                <FaqItem
                  key={faq.q}
                  faq={faq}
                  open={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
                  testid={`faq-item-${i + 1}`}
                />
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default EarlyAccess;
